import { Link } from 'expo-router';
import Clock from 'lucide-react-native/icons/clock';
import Star from 'lucide-react-native/icons/star';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { CardImage } from '@/components/riviera/card-image';
import { Badge } from '@/components/ui/badge';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { currencySymbol } from '@todo/core/i18n/format';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { sizedTourImage, type Tour } from '@todo/core/tours';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

// Tapping a card opens the in-app detail screen (/[lang]/tours/[id]); the
// actual booking (and our affiliate commission) happens from there, out on
// Viator.
export function TourCard({ tour, eager = false }: { tour: Tour; eager?: boolean }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <Link
      href={{ pathname: '/[lang]/tours/[id]', params: { lang: locale, id: tour.id } }}
      asChild>
      <Pressable
        style={({ pressed }) => [styles.cardPressable, pressed && { opacity: 0.85 }]}>
      <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={styles.imageBox}>
          <CardImage
            // 2x the 240x140 card box — matches the retina convention used
            // for article card images (see article-card.tsx).
            uri={sizedTourImage(tour.image_url, 480, 280)}
            alt={localizedTourTitle(tour, locale)}
            eager={eager}
          />
          <View style={styles.badgePos}>
            <Badge variant="gold">{t('tours.badge')}</Badge>
          </View>
        </View>
        <View style={styles.body}>
          <Text
            numberOfLines={2}
            style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {localizedTourTitle(tour, locale)}
          </Text>
          {tour.rating != null ? (
            <View style={styles.metaRow}>
              <Star size={13} color={theme.gold} fill={theme.gold} />
              <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
                {tour.rating.toFixed(1)}
                {tour.review_count != null ? ` (${tour.review_count})` : ''}
              </Text>
            </View>
          ) : null}
          {tour.duration ? (
            <View style={styles.metaRow}>
              <Clock size={13} color={theme.mutedForeground} />
              <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
                {tour.duration}
              </Text>
            </View>
          ) : null}
          <View style={styles.footerRow}>
            <Text style={[styles.price, { color: theme.gold }]}>
              {formatPrice(tour.from_price, tour.currency, t)}
            </Text>
            <Text style={[styles.attribution, { color: theme.mutedForeground }]}>
              {t('tours.viaViator')}
            </Text>
          </View>
        </View>
      </View>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  cardPressable: {
    width: 240,
    flexGrow: 0,
    flexShrink: 0,
  },
  card: {
    width: 240,
    borderRadius: Radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  imageBox: {
    height: 140,
    width: '100%',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  badgePos: {
    position: 'absolute',
    top: Spacing.two,
    left: Spacing.two,
  },
  body: {
    padding: Spacing.three - 2,
    gap: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    minHeight: 40,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
  footerRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  price: {
    fontSize: 13,
    fontWeight: '700',
  },
  attribution: {
    fontSize: 10,
    fontWeight: '500',
  },
});
